/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { Logger, MessageService } from '@core/lib/services';
import { TranslateService } from '@ngx-translate/core';
import { NotifyService } from '@app/common/notify.service';
import { ProxyDataService } from '@app/core/http';
import { BehaviorSubject } from 'rxjs';
import { moveItemInArray } from '@angular/cdk/drag-drop';

const log = new Logger('PinnedBarService');

@Injectable({
  providedIn: 'root'
})
export class PinnedBarService {
  private pinnedWorkLists = {};
  private workListOrderArray = [];
  private status = {
    isInProgress: false,
    errorWhileRetrievingPreference: false
  };

  private workListToBeDeleted;

  private pinnedWorkListObserver$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  //private changePinnedListObserver$: BehaviorSubject<any> = new BehaviorSubject(undefined);

  constructor(
    private userPreferenceService: UserPreferenceService,
    private notify: NotifyService,
    private messageService: MessageService,
    private dataService: ProxyDataService,
    private translate: TranslateService
  ) {
    this.broadcastListeners();
  }

  initialize() {
    if (_.isEmpty(this.workListOrderArray)) {
      const persistedPinnedOrder = this.userPreferenceService.getUserLevelPreferenceFromCache('PinnedOrder');
      _.each(persistedPinnedOrder, item => {
        this.workListOrderArray.push(item);
      });
    }
  }

  getPinnedListObserver() {
    return this.pinnedWorkListObserver$.asObservable();
  }

  /*getChangeListObserver() {
    return this.changePinnedListObserver$.asObservable();
  }*/

  getPinnedList() {
    return this.pinnedWorkLists;
  }

  getPinnedListOrder() {
    return this.workListOrderArray;
  }

  setPin(list: any, pinValue: boolean) {
    const sendValue = !!pinValue;
    const actionMessage = pinValue ? 'pinnedWorklist.errorMsg2' : 'pinnedWorklist.errorMsg3';

    if (list && list.id) {
      if (this.workListToBeDeleted === list.id) {
        this.notify.error(this.translate.instant('workloadManager.listContainer.error.CAToolWorklistDeletion'));
      } else {
        this.setPinValue(list.worklistId, pinValue)
          .then(
            () => {
              list.isPinned = pinValue;
              if (sendValue) {
                this.addWorkListToPinnedBar(list);
              } else {
                const index = this.workListOrderArray.indexOf(list.worklistId);
                if (index > -1) {
                  this.removeWorkListFromPinnedBar(index);
                }
              }
              this.messageService.broadcast('list.navigator.update');
            },
            error => {
              log.error(error);
              this.notify.error(
                [
                  this.translate.instant('pinnedWorklist.errorMsg1'),
                  list.displayName,
                  this.translate.instant(actionMessage),
                  this.translate.instant('pinnedWorklist.errorMsg4')
                ].join('')
              );
            }
          )
          .finally(() => {
            // vm.processing = false;
          });
      }
    }
  }

  setPinValue(workListId: string, pinValue: boolean) {
    return this.dataService.update('worklists', workListId, { pinned: !!pinValue });
  }

  changeWorkList(id) {
    const workList = this.pinnedWorkLists[id];
    if (workList && !workList.isWorklistDisabled) {
      this.messageService.broadcast('list.display', { list: workList });
      this.messageService.broadcast('onWorklistChange');
    }
  }

  addWorkListToPinnedBar(workList) {
    this.initialize();
    this.pinnedWorkLists[workList.worklistId] = workList;
    if (this.workListOrderArray && this.workListOrderArray.indexOf(workList.worklistId) < 0) {
      this.workListOrderArray.push(workList.worklistId);
      this.createUpdatePinnedOrderPreference();
    }

    this.pinnedWorkListObserver$.next(true);
  }

  removeWorkListFromPinnedBar(index: number) {
    if (index >= 0) {
      const workListId = this.workListOrderArray[index];
      delete this.pinnedWorkLists[workListId];
      this.workListOrderArray.splice(index, 1);
      this.createUpdatePinnedOrderPreference();
      this.pinnedWorkListObserver$.next(true);
    }
  }

  removeWorkListFromPinnedBarById(workListId: string) {
    const index = workListId && this.workListOrderArray.indexOf(workListId);
    if (index >= 0) {
      delete this.pinnedWorkLists[workListId];
      this.workListOrderArray.splice(index, 1);
      this.createUpdatePinnedOrderPreference();
      this.pinnedWorkListObserver$.next(true);
    }
  }

  changeWorkListOrder(previousIndex: number, currentIndex: number) {
    this.cleanupOrderArray();
    moveItemInArray(this.workListOrderArray, previousIndex, currentIndex);
    this.createUpdatePinnedOrderPreference();
    this.pinnedWorkListObserver$.next(true);
  }

  private cleanupOrderArray() {
    this.workListOrderArray.forEach((id, index) => {
      if (!this.pinnedWorkLists[id]) {
        this.workListOrderArray.splice(index, 1);
      }
    });
  }

  unpinWorkListFromPinnedBar(index: number) {
    const workList = this.pinnedWorkLists[this.workListOrderArray[index]];
    this.status.isInProgress = true;
    this.setPinValue(workList.worklistId, false)
      .then(
        () => {
          workList.isPinned = false;
          this.removeWorkListFromPinnedBar(index);
          this.messageService.broadcast('list.navigator.update');
        },
        error => {
          log.error(error);
          this.notify.error(
            [
              this.translate.instant('pinnedWorklist.errorMsg1'),
              this.translate.instant(workList.displayName),
              this.translate.instant('pinnedWorklist.errorMsg3'),
              this.translate.instant('pinnedWorklist.errorMsg4')
            ].join('')
          );
        }
      )
      .finally(() => {
        this.status.isInProgress = false;
      });
  }

  getPinnedWorklistsById(id) {
    return this.pinnedWorkLists[id];
  }

  createUpdatePinnedOrderPreference() {
    if (this.status.errorWhileRetrievingPreference) {
      return;
    }

    const pinnedOrderUserPreference = { PinnedOrder: this.workListOrderArray };
    this.userPreferenceService.createOrUpdateUserLevelPreference(pinnedOrderUserPreference);
  }

  private broadcastListeners() {
    this.messageService.on('worklist.deleted').subscribe(worklistId => {
      this.workListToBeDeleted = worklistId;
    });
  }
}
